/**
 * Vorbello LiveKit Token Helper
 * Generates cryptographically signed JWT Access Tokens for LiveKit SFU WebRTC
 * using browser-native Web Crypto API (HMAC-SHA256).
 */

export interface LiveKitTokenOptions {
  apiKey?: string;
  apiSecret?: string;
  roomName: string;
  name?: string;
  identity?: string;
  metadata?: string;
}

export function getStableClientDeviceId(): string {
  try {
    let id = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_device_client_id') : null;
    if (!id) {
      id = `dev-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 8)}`;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('vorbello_device_client_id', id);
      }
    }
    return id;
  } catch {
    return 'dev-default';
  }
}

export async function createLiveKitToken(
  optsOrApiKey: LiveKitTokenOptions | string = 'devkey',
  apiSecretArg: string = 'secret9876543210abcdef',
  roomNameArg?: string,
  participantNameArg?: string,
  participantIdentityArg?: string,
  metadataArg?: string
): Promise<string> {
  let apiKey = 'devkey';
  let apiSecret = 'secret9876543210abcdef';
  let roomName = '';
  let participantName = 'Participant';
  let participantIdentity = '';
  let metadata = '';

  if (typeof optsOrApiKey === 'object' && optsOrApiKey !== null) {
    apiKey = optsOrApiKey.apiKey || 'devkey';
    apiSecret = optsOrApiKey.apiSecret || 'secret9876543210abcdef';
    roomName = optsOrApiKey.roomName;
    participantName = optsOrApiKey.name || 'Participant';
    participantIdentity = optsOrApiKey.identity || '';
    metadata = optsOrApiKey.metadata || '';
  } else {
    apiKey = optsOrApiKey;
    apiSecret = apiSecretArg;
    roomName = roomNameArg || 'vorbello-room';
    participantName = participantNameArg || 'Participant';
    participantIdentity = participantIdentityArg || '';
    metadata = metadataArg || '';
  }

  // Safe UTF-8 Base64URL encoder that never throws on Romanian diacritics
  const b64url = (input: string | Uint8Array): string => {
    let uint8: Uint8Array;
    if (typeof input === 'string') {
      uint8 = new TextEncoder().encode(input);
    } else {
      uint8 = input;
    }
    let binary = '';
    const len = uint8.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(uint8[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  const header = JSON.stringify({ alg: 'HS256', typ: 'JWT' });
  const now = Math.floor(Date.now() / 1000);
  const identity = participantIdentity || `usr-${getStableClientDeviceId()}`;

  const payloadObj: any = {
    exp: now + 24 * 3600, // 24 hours valid
    nbf: now - 10,
    iss: apiKey,
    sub: identity,
    name: participantName,
    video: {
      room: roomName,
      roomJoin: true,
      canPublish: true,
      canSubscribe: true,
      canPublishData: true,
      canPublishSources: ['camera', 'microphone', 'screen_share', 'screen_share_audio'],
    },
  };
  if (metadata) {
    payloadObj.metadata = metadata;
  }
  const payload = JSON.stringify(payloadObj);

  const enc = new TextEncoder();
  const unsignedToken = `${b64url(header)}.${b64url(payload)}`;

  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    enc.encode(apiSecret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(unsignedToken));
  const signatureB64 = b64url(new Uint8Array(signature));

  return `${unsignedToken}.${signatureB64}`;
}

